import { useState, useEffect, useRef } from "react";
import { useOpenPortal, useCreateCheckout } from "@/hooks/useStripe";
import { getProPriceId } from "@/lib/stripe-price";
import { useAdminCheck } from "@/hooks/useAdmin";
import { useSubscriptionTier } from "@/hooks/useSubscriptionTier";
import { Link, useLocation } from "wouter";
import { useClerk, useUser } from "@clerk/react";
import {
  BookOpen,
  BookMarked,
  FileText,
  CheckSquare,
  Library,
  Users,
  UsersRound,
  FileBox,
  BarChart3,
  Download,
  HelpCircle,
  Lightbulb,
  LogOut,
  Menu,
  Settings2,
  PenLine,
  PanelLeftClose,
  PanelLeftOpen,
  CreditCard,
  Loader2,
  Lock,
  ChevronDown,
  ChevronUp,
  Eye,
  EyeOff,
  LayoutDashboard,
  ShieldCheck,
  Building2,
  Table2,
  Mail,
  ClipboardList,
  Archive,
  CalendarDays,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { FeatureRequestDialog } from "@/components/feature-request-dialog";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { useToast } from "@/hooks/use-toast";
import { getApiErrorMessage } from "@/lib/api-error";
import { useListTimetables, useListRecords, useGetProfile, useUpdateProfile, getGetProfileQueryKey, useGetSchoolMergeSuggestions } from "@workspace/api-client-react";
import { MergeWizard } from "@/components/merge-wizard";
import { FreshStartWizard } from "@/components/fresh-start-wizard";
import { ArrowRight, X as XIcon } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { useDiary } from "@/contexts/DiaryContext";

// ─── Account Dialog ───────────────────────────────────────────────────────────

function AccountDialog({
  open,
  onClose,
  onOpenMergeWizard,
  showMergeButton,
}: {
  open: boolean;
  onClose: () => void;
  onOpenMergeWizard: () => void;
  showMergeButton: boolean;
}) {
  const { user } = useUser();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const openPortal = useOpenPortal();
  const createCheckout = useCreateCheckout();
  const { data: tierData } = useSubscriptionTier();
  // Use hasActiveSubscription (not tier) so trial users see "Subscribe" not "Manage portal"
  const isSubscribed = (tierData as any)?.hasActiveSubscription === true;
  const betaAccessActive = (tierData as any)?.betaAccessActive === true;
  const { data: profileData } = useGetProfile();
  const profileName = (profileData as any)?.name?.trim() || "";

  const [firstName, setFirstName]         = useState("");
  const [lastName, setLastName]           = useState("");
  const [saving, setSaving]               = useState(false);

  const updateProfileMutation = useUpdateProfile();

  const [pwOpen, setPwOpen]               = useState(false);
  const [currentPw, setCurrentPw]         = useState("");
  const [newPw, setNewPw]                 = useState("");
  const [confirmPw, setConfirmPw]         = useState("");
  const [showCurrentPw, setShowCurrentPw] = useState(false);
  const [showNewPw, setShowNewPw]         = useState(false);
  const [pwSaving, setPwSaving]           = useState(false);

  const [emailOpen, setEmailOpen]         = useState(false);
  const [newEmail, setNewEmail]           = useState("");
  const [emailStep, setEmailStep]         = useState<"input" | "verify">("input");
  const [emailObj, setEmailObj]           = useState<any>(null);
  const [emailCode, setEmailCode]         = useState("");
  const [emailSaving, setEmailSaving]     = useState(false);

  useEffect(() => {
    if (open && user) {
      setFirstName(user.firstName ?? "");
      setLastName(user.lastName ?? "");
    }
    if (!open) {
      setPwOpen(false);
      setCurrentPw(""); setNewPw(""); setConfirmPw("");
      setShowCurrentPw(false); setShowNewPw(false);
      setEmailOpen(false);
      setNewEmail(""); setEmailCode(""); setEmailObj(null); setEmailStep("input");
    }
  }, [open, user]);

  const primaryEmail = user?.primaryEmailAddress?.emailAddress ?? "";

  const handleSave = async () => {
    if (!user) return;
    const newFirst = firstName.trim();
    const newLast  = lastName.trim();
    if (!newFirst) {
      toast({ title: "First name is required", variant: "destructive" });
      return;
    }
    const fullName = [newFirst, newLast].filter(Boolean).join(" ");
    setSaving(true);
    try {
      // Always update the ClassTrack profile name — this is the source of truth in the app.
      await updateProfileMutation.mutateAsync({ data: { name: fullName } });
      // Invalidate the profile cache so the sidebar name refreshes immediately.
      await queryClient.invalidateQueries({ queryKey: getGetProfileQueryKey() });

      // Also try to sync Clerk's firstName/lastName. Some account types (e.g. Google OAuth)
      // don't allow this — swallow any Clerk error silently.
      try {
        await user.update({ firstName: newFirst, lastName: newLast || undefined });
      } catch {
        // Clerk rejected the name change — that's fine, our profile is already saved.
      }

      toast({ title: "Account updated" });
      onClose();
    } catch (err: any) {
      // api-error-helper-exempt: Clerk SDK error shape (err.errors[0].longMessage), not ApiError.
      const msg =
        err?.errors?.[0]?.longMessage ??
        err?.errors?.[0]?.message ??
        err?.message ??
        "Failed to save changes";
      toast({ title: msg, variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  const handleSendEmailCode = async () => {
    if (!user) return;
    const trimmed = newEmail.trim().toLowerCase();
    if (!trimmed || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      toast({ title: "Please enter a valid email address", variant: "destructive" });
      return;
    }
    if (trimmed === primaryEmail.toLowerCase()) {
      toast({ title: "That's already your current email", variant: "destructive" });
      return;
    }
    setEmailSaving(true);
    try {
      const created = await user.createEmailAddress({ email: trimmed });
      await created.prepareVerification({ strategy: "email_code" });
      setEmailObj(created);
      setEmailStep("verify");
      toast({ title: "Verification code sent", description: `Check ${trimmed} for a 6-digit code.` });
    } catch (err: any) {
      // api-error-helper-exempt: Clerk SDK error shape (err.errors[0].longMessage), not ApiError.
      const msg = err?.errors?.[0]?.longMessage ?? err?.errors?.[0]?.message ?? err?.message ?? "Failed to send code";
      toast({ title: msg, variant: "destructive" });
    } finally {
      setEmailSaving(false);
    }
  };

  const handleVerifyEmailCode = async () => {
    if (!user || !emailObj) return;
    if (!emailCode.trim()) {
      toast({ title: "Please enter the verification code", variant: "destructive" });
      return;
    }
    setEmailSaving(true);
    try {
      await emailObj.attemptVerification({ code: emailCode.trim() });
      await user.update({ primaryEmailAddressId: emailObj.id });
      await user.reload();
      toast({ title: "Email updated successfully" });
      setEmailOpen(false);
      setNewEmail(""); setEmailCode(""); setEmailObj(null); setEmailStep("input");
    } catch (err: any) {
      // api-error-helper-exempt: Clerk SDK error shape (err.errors[0].longMessage), not ApiError.
      const msg = err?.errors?.[0]?.longMessage ?? err?.errors?.[0]?.message ?? err?.message ?? "Verification failed";
      toast({ title: msg, variant: "destructive" });
    } finally {
      setEmailSaving(false);
    }
  };

  const handleChangePassword = async () => {
    if (!user) return;
    if (newPw.length < 8) {
      toast({ title: "Password must be at least 8 characters", variant: "destructive" });
      return;
    }
    if (newPw !== confirmPw) {
      toast({ title: "Passwords do not match", variant: "destructive" });
      return;
    }
    setPwSaving(true);
    try {
      await user.updatePassword({ currentPassword: currentPw, newPassword: newPw });
      toast({ title: "Password updated" });
      setCurrentPw(""); setNewPw(""); setConfirmPw("");
      setPwOpen(false);
    } catch (err: any) {
      // api-error-helper-exempt: Clerk SDK error shape (err.errors[0].longMessage), not ApiError.
      const msg = err?.errors?.[0]?.longMessage ?? err?.message ?? "Failed to update password";
      toast({ title: msg, variant: "destructive" });
    } finally {
      setPwSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Account</DialogTitle>
          <DialogDescription className="sr-only">Manage your ClassTrack account settings</DialogDescription>
        </DialogHeader>

        {/* Avatar + identity row */}
        <div className="flex items-center gap-4 py-1">
          <Avatar className="h-14 w-14 flex-shrink-0">
            <AvatarImage src={user?.imageUrl} />
            <AvatarFallback className="bg-primary/10 text-primary text-lg font-semibold">
              {firstName.charAt(0) || profileName.charAt(0) || "?"}
            </AvatarFallback>
          </Avatar>
          <div className="min-w-0">
            <p className="font-semibold text-gray-900 truncate">
              {[firstName, lastName].filter(Boolean).join(" ") || profileName || "My Account"}
            </p>
            <p className="text-sm text-gray-500 truncate">{primaryEmail}</p>
          </div>
        </div>

        <Separator />

        {/* Name fields */}
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs text-gray-500">First name</Label>
              <Input
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                placeholder="First name"
                className="h-9"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-gray-500">Last name</Label>
              <Input
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                placeholder="Last name"
                className="h-9"
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs text-gray-500">Email address</Label>
            <div className="flex items-center h-9 px-3 rounded-md border border-input bg-gray-50">
              <span className="text-sm text-gray-500 truncate">{primaryEmail}</span>
            </div>
          </div>
        </div>

        <Separator />

        {/* Password section */}
        <div>
          <button
            type="button"
            onClick={() => setPwOpen((v) => !v)}
            className="flex w-full items-center justify-between py-1 text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors"
          >
            <span className="flex items-center gap-2">
              <Lock className="h-4 w-4 text-gray-400" />
              Change password
            </span>
            {pwOpen ? <ChevronUp className="h-4 w-4 text-gray-400" /> : <ChevronDown className="h-4 w-4 text-gray-400" />}
          </button>

          {pwOpen && (
            <div className="mt-3 space-y-2.5">
              {/* Current password */}
              <div className="space-y-1.5">
                <Label className="text-xs text-gray-500">Current password</Label>
                <div className="relative">
                  <Input
                    type={showCurrentPw ? "text" : "password"}
                    value={currentPw}
                    onChange={(e) => setCurrentPw(e.target.value)}
                    placeholder="Current password"
                    className="h-9 pr-9"
                  />
                  <button
                    type="button"
                    onClick={() => setShowCurrentPw((v) => !v)}
                    className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showCurrentPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              {/* New + confirm in a grid */}
              <div className="grid grid-cols-2 gap-2.5">
                <div className="space-y-1.5">
                  <Label className="text-xs text-gray-500">New password</Label>
                  <div className="relative">
                    <Input
                      type={showNewPw ? "text" : "password"}
                      value={newPw}
                      onChange={(e) => setNewPw(e.target.value)}
                      placeholder="Min 8 characters"
                      className="h-9 pr-9"
                    />
                    <button
                      type="button"
                      onClick={() => setShowNewPw((v) => !v)}
                      className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      {showNewPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs text-gray-500">Confirm password</Label>
                  <Input
                    type="password"
                    value={confirmPw}
                    onChange={(e) => setConfirmPw(e.target.value)}
                    placeholder="Repeat password"
                    className={`h-9 ${confirmPw && confirmPw !== newPw ? "border-red-400 focus-visible:ring-red-400" : ""}`}
                  />
                </div>
              </div>

              {confirmPw && confirmPw !== newPw && (
                <p className="text-xs text-red-500">Passwords do not match</p>
              )}

              <Button
                size="sm"
                variant="outline"
                className="w-full mt-1"
                onClick={handleChangePassword}
                disabled={pwSaving || !currentPw || !newPw || !confirmPw}
              >
                {pwSaving && <Loader2 className="h-3.5 w-3.5 mr-2 animate-spin" />}
                Update password
              </Button>
            </div>
          )}
        </div>

        <Separator />

        {/* Change email */}
        <div>
          <button
            type="button"
            onClick={() => { setEmailOpen((v) => !v); if (emailOpen) { setNewEmail(""); setEmailCode(""); setEmailObj(null); setEmailStep("input"); } }}
            className="flex w-full items-center justify-between py-1 text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors"
          >
            <span className="flex items-center gap-2">
              <Mail className="h-4 w-4 text-gray-400" />
              Change email
            </span>
            {emailOpen ? <ChevronUp className="h-4 w-4 text-gray-400" /> : <ChevronDown className="h-4 w-4 text-gray-400" />}
          </button>

          {emailOpen && (
            <div className="mt-3 space-y-2.5">
              {emailStep === "input" ? (
                <>
                  <div className="space-y-1.5">
                    <Label className="text-xs text-gray-500">New email address</Label>
                    <Input
                      type="email"
                      value={newEmail}
                      onChange={(e) => setNewEmail(e.target.value)}
                      placeholder="new@example.com"
                      className="h-9"
                    />
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    className="w-full mt-1"
                    onClick={handleSendEmailCode}
                    disabled={emailSaving || !newEmail.trim()}
                  >
                    {emailSaving && <Loader2 className="h-3.5 w-3.5 mr-2 animate-spin" />}
                    Send verification code
                  </Button>
                </>
              ) : (
                <>
                  <p className="text-xs text-gray-500">
                    A 6-digit code was sent to <span className="font-medium text-gray-700">{newEmail}</span>. Enter it below to confirm the change.
                  </p>
                  <div className="space-y-1.5">
                    <Label className="text-xs text-gray-500">Verification code</Label>
                    <Input
                      type="text"
                      inputMode="numeric"
                      maxLength={6}
                      value={emailCode}
                      onChange={(e) => setEmailCode(e.target.value.replace(/\D/g, ""))}
                      placeholder="123456"
                      className="h-9 tracking-widest text-center font-mono"
                    />
                  </div>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="ghost"
                      className="flex-1"
                      onClick={() => { setEmailStep("input"); setEmailCode(""); setEmailObj(null); }}
                      disabled={emailSaving}
                    >
                      Back
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="flex-1"
                      onClick={handleVerifyEmailCode}
                      disabled={emailSaving || emailCode.length < 6}
                    >
                      {emailSaving && <Loader2 className="h-3.5 w-3.5 mr-2 animate-spin" />}
                      Verify &amp; update
                    </Button>
                  </div>
                </>
              )}
            </div>
          )}
        </div>

        <Separator />

        {showMergeButton && (
          <Button
            variant="outline"
            className="w-full justify-start gap-2 h-10"
            onClick={() => { onClose(); onOpenMergeWizard(); }}
            data-testid="button-open-merge-wizard"
          >
            <Building2 className="h-4 w-4 text-indigo-600" />
            Review school data merge
          </Button>
        )}

        {/* Manage subscription / beta notice */}
        {betaAccessActive ? (
          <div className="w-full px-3 py-2.5 rounded-lg border border-teal-200 bg-teal-50">
            <p className="text-sm font-medium text-teal-800">Founding beta access</p>
            <p className="text-xs text-teal-700 mt-0.5">No payment needed — access until 1 Jan 2027.</p>
          </div>
        ) : isSubscribed ? (
          <Button
            variant="outline"
            className="w-full justify-start gap-2 h-10"
            disabled={openPortal.isPending}
            onClick={async () => {
              try {
                const { url } = await openPortal.mutateAsync();
                window.location.href = url;
              } catch (err) {
                toast({
                  title: "Could not open billing portal",
                  description: getApiErrorMessage(err, "Please try again."),
                  variant: "destructive",
                });
              }
            }}
          >
            {openPortal.isPending
              ? <Loader2 className="h-4 w-4 animate-spin text-primary" />
              : <CreditCard className="h-4 w-4 text-primary" />
            }
            Manage Subscription
          </Button>
        ) : (
          <Button
            variant="outline"
            className="w-full justify-start gap-2 h-10"
            disabled={createCheckout.isPending}
            onClick={async () => {
              onClose();
              try {
                const result = await createCheckout.mutateAsync(getProPriceId());
                if (result.url) window.location.href = result.url;
              } catch (err) {
                toast({
                  title: "Could not start checkout",
                  description: getApiErrorMessage(err, "Please try again."),
                  variant: "destructive",
                });
              }
            }}
          >
            {createCheckout.isPending
              ? <Loader2 className="h-4 w-4 animate-spin text-primary" />
              : <CreditCard className="h-4 w-4 text-primary" />
            }
            Subscribe to Pro
          </Button>
        )}

        {/* Actions */}
        <div className="flex gap-2 pt-1">
          <Button variant="outline" className="flex-1" onClick={onClose}>
            Cancel
          </Button>
          <Button className="flex-1" onClick={handleSave} disabled={saving}>
            {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Save changes
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ─── AppLayout ────────────────────────────────────────────────────────────────

export default function AppLayout({ children }: { children: React.ReactNode }) {
  const [location, setLocation] = useLocation();
  const { signOut } = useClerk();
  const { user } = useUser();
  const queryClient = useQueryClient();
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [collapsed, setCollapsed]       = useState(false);

  // Close the mobile drawer automatically whenever the user navigates.
  // The Link components in SidebarContent use Wouter's <Link> which navigates
  // but never calls setIsMobileOpen(false), so we watch location instead.
  useEffect(() => {
    setIsMobileOpen(false);
  }, [location]);
  const [accountOpen, setAccountOpen]   = useState(false);
  // Task #138 — feature request capture (footer button + dialog).
  const [featureRequestOpen, setFeatureRequestOpen] = useState(false);
  const [mergeWizardOpen, setMergeWizardOpen] = useState(false);
  const [freshStartOpen, setFreshStartOpen] = useState(false);
  const [freshStartAutoChecked, setFreshStartAutoChecked] = useState(false);
  const updateProfile = useUpdateProfile();

  // Clear all cached query data when the logged-in user changes (e.g. switching accounts)
  const prevUserIdRef = useRef<string | null | undefined>(undefined);
  useEffect(() => {
    const currentId = user?.id ?? null;
    if (prevUserIdRef.current !== undefined && prevUserIdRef.current !== currentId) {
      queryClient.clear();
    }
    prevUserIdRef.current = currentId;
  }, [user?.id, queryClient]);

  const { data: adminCheckData } = useAdminCheck(user?.id);
  const isAdmin = (adminCheckData as any)?.isAdmin === true;
  const { data: tierData } = useSubscriptionTier();
  const isTrial = (tierData as any)?.isTrial ?? false;
  const trialDaysLeft = (tierData as any)?.trialDaysLeft ?? 0;
  const hasAccess = (tierData as any)?.hasAccess ?? true;
  const inReadOnlyMode = tierData != null && hasAccess === false;
  const paymentFailed = (tierData as any)?.paymentFailed === true;
  const openPortalForBanner = useOpenPortal();
  const { toast: appLayoutToast } = useToast();
  // Banner is dismissible per browser session — once the user closes it we
  // hide the strip but keep the global read-only enforcement (data-readonly
  // attribute, disabled write buttons, server 403s) in place.
  const [readOnlyBannerDismissed, setReadOnlyBannerDismissed] = useState(() => {
    if (typeof window === "undefined") return false;
    return sessionStorage.getItem("classtrack:readonly-banner-dismissed") === "1";
  });
  const showReadOnlyBanner = inReadOnlyMode && !readOnlyBannerDismissed;
  const dismissReadOnlyBanner = () => {
    if (typeof window !== "undefined") {
      sessionStorage.setItem("classtrack:readonly-banner-dismissed", "1");
    }
    setReadOnlyBannerDismissed(true);
  };

  // Toggle a global `data-readonly` attribute on <html> so any element
  // tagged with `data-write-action` (Add/Edit/Delete buttons across the
  // app) gets visually disabled and stops responding to clicks. Server
  // enforcement (requireActiveAccess) is the source of truth — this is the
  // affordance, not the gate. We also stamp a `title` on each tagged
  // element so users get a "Subscription ended" tooltip on hover, and
  // observe DOM mutations so newly mounted buttons (page navigation,
  // dialog opens, etc.) pick the title up too.
  useEffect(() => {
    const html = document.documentElement;
    const tooltip = "Subscription ended — your account is read-only. Subscribe to make changes.";

    if (!inReadOnlyMode) {
      html.removeAttribute("data-readonly");
      // Best-effort cleanup of any stale tooltips we added earlier.
      document.querySelectorAll('[data-write-action][data-readonly-title="1"]').forEach((el) => {
        el.removeAttribute("title");
        el.removeAttribute("data-readonly-title");
      });
      return;
    }

    html.setAttribute("data-readonly", "true");

    const stamp = (root: ParentNode) => {
      root.querySelectorAll('[data-write-action]').forEach((el) => {
        if (!el.hasAttribute("title") || el.getAttribute("data-readonly-title") === "1") {
          el.setAttribute("title", tooltip);
          el.setAttribute("data-readonly-title", "1");
        }
      });
    };

    stamp(document);

    const observer = new MutationObserver((mutations) => {
      for (const m of mutations) {
        m.addedNodes.forEach((n) => {
          if (n.nodeType === Node.ELEMENT_NODE) {
            const el = n as Element;
            if (el.matches?.("[data-write-action]")) {
              if (!el.hasAttribute("title") || el.getAttribute("data-readonly-title") === "1") {
                el.setAttribute("title", tooltip);
                el.setAttribute("data-readonly-title", "1");
              }
            }
            stamp(el);
          }
        });
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });

    return () => {
      observer.disconnect();
      html.removeAttribute("data-readonly");
      document.querySelectorAll('[data-write-action][data-readonly-title="1"]').forEach((el) => {
        el.removeAttribute("title");
        el.removeAttribute("data-readonly-title");
      });
    };
  }, [inReadOnlyMode]);
  const { data: profileData } = useGetProfile();
  // Beta gate: when closed-beta mode is active (isClosedBeta=true from
  // /subscription/tier) and the user's betaAccessUntil is not in the future,
  // replace the main content area with a "pending approval" screen.
  // tierData being null (loading) means we don't yet know → no flash.
  // Admin users bypass the gate regardless of beta status.
  const isClosedBeta = (tierData as any)?.isClosedBeta === true;
  const betaAccessActive = (tierData as any)?.betaAccessActive === true;
  const isBetaPending = tierData != null && isClosedBeta && !betaAccessActive && !isAdmin;
  const isHeadTeacher = profileData?.schoolRole === "head_teacher" && !!profileData?.schoolCode;
  const profileSchoolCode = profileData?.schoolCode ?? undefined;
  const mergeWizardPrefs =
    profileData?.preferences && typeof profileData.preferences === "object"
      ? (profileData.preferences as Record<string, unknown>)
      : {};
  const rawMergeStatus = mergeWizardPrefs.mergeWizardStatus;
  const mergeWizardStatus: "pending" | "dismissed" | "completed" | undefined =
    rawMergeStatus === "pending" ||
    rawMergeStatus === "dismissed" ||
    rawMergeStatus === "completed"
      ? rawMergeStatus
      : undefined;
  // Only ever ask the API for suggestions when this user belongs to a school
  // and has not yet completed/dismissed the wizard. This prevents showing the
  // banner to teachers who joined after the school set up (their suggestions
  // list will be empty since they own no private students to merge).
  const shouldQuerySuggestions =
    !!profileSchoolCode && mergeWizardStatus !== "completed";
  const { data: mergeData } = useGetSchoolMergeSuggestions({
    query: { enabled: shouldQuerySuggestions },
  });
  const suggestionCount = mergeData?.suggestions?.length ?? 0;
  const showMergeBanner =
    shouldQuerySuggestions && mergeWizardStatus !== "dismissed" && suggestionCount > 0;
  // Always available for school-linked users so they can review or revisit
  // their merges at any time, regardless of the wizard's transient state.
  const showMergeAccountButton = !!profileSchoolCode;

  // ─── Fresh-start wizard eligibility ─────────────────────────────────────
  // Surfaced when the server flagged the user as eligible (school-linked AND
  // still owns at least one non-archived private timetable/class/course)
  // AND they haven't already completed/dismissed the wizard. Same
  // "preferences bag" pattern as mergeWizardStatus above so we don't need a
  // schema migration for a single status flag.
  const rawFreshStartStatus = mergeWizardPrefs.freshStartStatus;
  const freshStartStatus: "pending" | "dismissed" | "completed" | undefined =
    rawFreshStartStatus === "pending" ||
    rawFreshStartStatus === "dismissed" ||
    rawFreshStartStatus === "completed"
      ? rawFreshStartStatus
      : undefined;
  const profileFreshStartEligible =
    profileData && (profileData as { freshStartEligible?: boolean }).freshStartEligible === true;
  const freshStartShouldOpen =
    profileFreshStartEligible &&
    freshStartStatus !== "completed" &&
    freshStartStatus !== "dismissed";

  // Auto-open the wizard the first time the user becomes eligible
  // during this AppLayout mount. The latch is intentionally only set
  // *after* an actual auto-open — otherwise a not-yet-eligible profile
  // load would lock the wizard out of the session, and a teacher who
  // entered their school code from Settings wouldn't see fresh-start
  // pop without a full reload.
  useEffect(() => {
    if (freshStartAutoChecked) return;
    if (!profileData) return;                      // wait for profile load
    if (mergeWizardOpen || showMergeBanner) return; // merge wizard takes priority
    if (!freshStartShouldOpen) return;             // not eligible *yet* — keep watching
    setFreshStartOpen(true);
    setFreshStartAutoChecked(true);
  }, [
    freshStartAutoChecked,
    profileData,
    mergeWizardOpen,
    showMergeBanner,
    freshStartShouldOpen,
  ]);

  // Settings page dispatches this event when the user clicks "Re-run
  // school year setup" — listening here keeps the wizard mounted in one
  // place and avoids URL-state coupling between the pages.
  useEffect(() => {
    const handler = () => setFreshStartOpen(true);
    window.addEventListener("classtrack:open-fresh-start", handler);
    return () => window.removeEventListener("classtrack:open-fresh-start", handler);
  }, []);
  const profileName = (profileData as any)?.name?.trim() || "";
  const { data: timetables = [] } = useListTimetables();
  const { activeTimetableId, setActiveTimetableId } = useDiary();
  const activeTimetables = (timetables as any[]).filter((t) => !t.archived);

  // Count records not yet uploaded to school system — drives the Records badge
  const { data: notEnteredRecords = [] } = useListRecords({ entered: "false" });
  const pendingSentralCount = (notEnteredRecords as any[]).length;

  const navigation = [
    { name: "Dashboard", href: "/app/dashboard",        icon: LayoutDashboard, badge: 0 },
    { name: "Diary",     href: "/app/diary",            icon: BookOpen,         badge: 0 },
    { name: "Timetable", href: "/app/timetable-settings", icon: CalendarDays,  badge: 0 },
    { name: "Lessons",   href: "/app/lessons",          icon: BookMarked,       badge: 0 },
    { name: "Notes",     href: "/app/notes",            icon: FileText,         badge: 0 },
    { name: "To Do",     href: "/app/tasks",            icon: CheckSquare,      badge: 0 },
    { name: "Subjects",  href: "/app/courses",          icon: Library,          badge: 0 },
    { name: "Students",  href: "/app/students",         icon: Users,            badge: 0 },
    { name: "Classes",   href: "/app/classes",          icon: UsersRound,       badge: 0 },
    { name: "Records",   href: "/app/records",          icon: FileBox,          badge: pendingSentralCount },
    { name: "Markbook",  href: "/app/markbook",         icon: Table2,           badge: 0 },
    { name: "Analytics", href: "/app/analytics",        icon: BarChart3,        badge: 0 },
    ...(isHeadTeacher ? [{ name: "School Overview", href: "/app/school-overview", icon: Building2, badge: 0 }] : []),
    ...(isHeadTeacher ? [{ name: "School Setup", href: "/app/school-setup", icon: Building2, badge: 0 }] : []),
    // Program Registration — hidden when feature flag is off (VITE_PROGRAM_REGISTRATION_ENABLED).
    // Also enabled on the staging deployment (hostname check) so smoke-testing
    // can proceed without changing production env vars.
    ...(import.meta.env.VITE_PROGRAM_REGISTRATION_ENABLED === "true" ||
        window.location.hostname === "class-track-staging.replit.app"
      ? [{ name: "Programs", href: "/app/program-registration", icon: ClipboardList, badge: 0 }]
      : []),
  ];

  const bottomNavigation = [
    { name: "Diaries",  href: "/app/diaries",  icon: BookOpen  },
    { name: "Exports",  href: "/app/exports",  icon: Download  },
    { name: "Archive",  href: "/app/archive",  icon: Archive   },
    { name: "Settings", href: "/app/settings", icon: Settings2 },
    { name: "Help",     href: "/app/help",     icon: HelpCircle },
    ...(isAdmin ? [{ name: "Admin", href: "/app/admin", icon: ShieldCheck }] : []),
  ];

  const SidebarContent = ({ isCollapsed = false }: { isCollapsed?: boolean }) => (
    <div className="flex h-full flex-col bg-sidebar border-r border-sidebar-border">
      {/* Logo + toggle */}
      <div className={`flex items-center border-b border-sidebar-border ${isCollapsed ? "justify-center p-3" : "p-4"}`}>
        {isCollapsed ? (
          <button
            onClick={() => setCollapsed(false)}
            title="Expand sidebar"
            className="text-sidebar-foreground/70 hover:text-sidebar-foreground transition-colors"
          >
            <PanelLeftOpen className="h-5 w-5" />
          </button>
        ) : (
          <>
            <PenLine className="h-6 w-6 text-sidebar-primary mr-2 flex-shrink-0" />
            <span className="text-xl font-bold tracking-tight text-sidebar-foreground flex-1">ClassTrack</span>
            <button
              onClick={() => setCollapsed(true)}
              title="Collapse sidebar"
              className="text-sidebar-foreground/50 hover:text-sidebar-foreground transition-colors ml-1 flex-shrink-0"
            >
              <PanelLeftClose className="h-5 w-5" />
            </button>
          </>
        )}
      </div>

      {/* Diary selector */}
      {!isCollapsed && (
        <div className="p-3 border-b border-sidebar-border">
          {activeTimetables.length > 0 ? (
            <Select
              value={activeTimetableId ? String(activeTimetableId) : undefined}
              onValueChange={(val) => {
                if (val === "__manage") { setLocation("/app/diaries"); return; }
                setActiveTimetableId(Number(val));
              }}
            >
              <SelectTrigger className="bg-secondary border-border text-foreground text-sm h-9 [&>svg]:text-muted-foreground">
                <SelectValue placeholder="Select diary" />
              </SelectTrigger>
              <SelectContent>
                {activeTimetables.map((t: any) => (
                  <SelectItem key={t.id} value={String(t.id)}>{t.name}</SelectItem>
                ))}
                <SelectItem value="__manage" className="text-primary font-medium border-t mt-1 pt-1">
                  Manage Diaries →
                </SelectItem>
              </SelectContent>
            </Select>
          ) : (
            <button
              onClick={() => setLocation("/app/diaries")}
              className="w-full text-left text-xs text-muted-foreground hover:text-foreground transition-colors px-1"
            >
              + Create first diary
            </button>
          )}
        </div>
      )}

      {/* Nav region — main nav + bottom nav share the remaining vertical
          space. The outer wrapper establishes the bounded flex context so
          both sections can scroll independently on short screens. */}
      <div className="flex-1 min-h-0 flex flex-col overflow-hidden">

      {/* Main nav — grows to fill the space, scrolls when the list is
          longer than the available height (e.g. School Overview items
          added for head teachers push the count beyond ~12). */}
      <div className={`flex-1 min-h-0 overflow-y-auto py-3`}>
        <nav className={`space-y-0.5 ${isCollapsed ? "px-1" : "px-2"}`}>
          {navigation.map((item) => {
            const isActive = location === item.href || location.startsWith(item.href + "/");
            return (
              <Link
                key={item.name}
                href={item.href}
                title={isCollapsed ? item.name : undefined}
                className={`flex items-center rounded-md transition-colors ${
                  isCollapsed ? "justify-center px-2 py-2.5" : "px-3 py-2 text-sm font-medium"
                } ${
                  isActive
                    ? "bg-sidebar-primary text-sidebar-primary-foreground"
                    : "text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
                }`}
              >
                {/* Icon with optional badge dot in collapsed mode */}
                <span className="relative flex-shrink-0">
                  <item.icon
                    className={`${isCollapsed ? "h-5 w-5" : "mr-3 h-4 w-4"} ${
                      isActive ? "text-sidebar-primary-foreground" : "text-sidebar-foreground/60"
                    }`}
                  />
                  {item.badge > 0 && isCollapsed && (
                    <span className="absolute -top-1 -right-1 bg-amber-400 rounded-full w-2 h-2" />
                  )}
                </span>
                {!isCollapsed && (
                  <span className="flex-1 flex items-center justify-between">
                    {item.name}
                    {item.badge > 0 && (
                      <span className="ml-2 inline-flex items-center justify-center bg-amber-400 text-amber-900 text-[10px] font-bold rounded-full w-5 h-5 leading-none flex-shrink-0">
                        {item.badge > 99 ? "99+" : item.badge}
                      </span>
                    )}
                  </span>
                )}
              </Link>
            );
          })}
        </nav>
      </div>

      {/* Bottom nav — pinned below the main nav, always visible at a
          minimum of 5rem, scrolls if the list is unusually long. */}
      <div className={`flex-none min-h-20 overflow-y-auto border-t border-sidebar-border ${isCollapsed ? "p-1" : "p-3"}`}>
        <nav className="space-y-0.5">
          {bottomNavigation.map((item) => (
            <Link
              key={item.name}
              href={item.href}
              title={isCollapsed ? item.name : undefined}
              className={`flex items-center rounded-md text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground transition-colors ${
                isCollapsed ? "justify-center px-2 py-2.5" : "px-3 py-2 text-sm font-medium"
              }`}
            >
              <item.icon className={`flex-shrink-0 text-sidebar-foreground/60 ${isCollapsed ? "h-5 w-5" : "mr-3 h-4 w-4"}`} />
              {!isCollapsed && item.name}
            </Link>
          ))}
          {/* Task #138 — feature request capture */}
          <button
            onClick={() => setFeatureRequestOpen(true)}
            title={isCollapsed ? "Suggest a feature" : undefined}
            className={`flex w-full items-center rounded-md text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground transition-colors ${
              isCollapsed ? "justify-center px-2 py-2.5" : "px-3 py-2 text-sm font-medium"
            }`}
          >
            <Lightbulb className={`flex-shrink-0 text-sidebar-foreground/60 ${isCollapsed ? "h-5 w-5" : "mr-3 h-4 w-4"}`} />
            {!isCollapsed && "Suggest a feature"}
          </button>
          <button
            onClick={() => { queryClient.clear(); signOut(() => setLocation("/")); }}
            title={isCollapsed ? "Sign Out" : undefined}
            className={`flex w-full items-center rounded-md text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground transition-colors ${
              isCollapsed ? "justify-center px-2 py-2.5" : "px-3 py-2 text-sm font-medium"
            }`}
          >
            <LogOut className={`flex-shrink-0 text-sidebar-foreground/60 ${isCollapsed ? "h-5 w-5" : "mr-3 h-4 w-4"}`} />
            {!isCollapsed && "Sign Out"}
          </button>
        </nav>
      </div>
      </div>{/* end nav region */}

      {/* User */}
      <button
        type="button"
        onClick={() => setAccountOpen(true)}
        title={isCollapsed ? (profileName || user?.fullName || "My Account") : "Manage account"}
        className={`flex items-center border-t border-sidebar-border w-full text-left hover:bg-sidebar-accent transition-colors ${isCollapsed ? "justify-center p-3" : "p-4"}`}
      >
        <Avatar className="h-8 w-8 flex-shrink-0">
          <AvatarImage src={user?.imageUrl} />
          <AvatarFallback className="bg-sidebar-primary/20 text-sidebar-primary text-xs font-semibold">
            {profileName.charAt(0) || user?.firstName?.charAt(0) || "?"}
          </AvatarFallback>
        </Avatar>
        {!isCollapsed && (
          <div className="ml-3 min-w-0">
            <p className="text-sm font-medium text-sidebar-foreground truncate">{profileName || user?.fullName || "My Account"}</p>
            <p className="text-xs text-muted-foreground truncate">Manage account</p>
          </div>
        )}
      </button>
    </div>
  );

  return (
    <div className="flex h-screen bg-background relative">
      {/* Account dialog */}
      <AccountDialog
        open={accountOpen}
        onClose={() => setAccountOpen(false)}
        onOpenMergeWizard={() => setMergeWizardOpen(true)}
        showMergeButton={showMergeAccountButton}
      />

      {/* Task #138 — feature request capture */}
      <FeatureRequestDialog
        open={featureRequestOpen}
        onOpenChange={setFeatureRequestOpen}
      />

      {/* School merge wizard */}
      <MergeWizard
        open={mergeWizardOpen}
        onClose={() => setMergeWizardOpen(false)}
        onCompleted={() => {
          // Chain into the fresh-start wizard so a teacher who joins a
          // school flows from "match my private students" straight into
          // "wind down my old diary". The eligibility gate inside the
          // wizard's open prop ensures we don't pop it for users who have
          // nothing to wind down or who already finished/dismissed it.
          if (freshStartShouldOpen) setFreshStartOpen(true);
        }}
      />

      {/* Fresh-start wizard — wind down the user's pre-school diary +
          private classes/courses on first join, or whenever they re-run
          it from Settings. */}
      <FreshStartWizard
        open={freshStartOpen}
        onClose={() => setFreshStartOpen(false)}
      />

      {/* Mobile sidebar */}
      <Sheet open={isMobileOpen} onOpenChange={setIsMobileOpen}>
        <SheetContent side="left" className="p-0 w-64 bg-sidebar border-r border-sidebar-border">
          <SidebarContent />
        </SheetContent>
      </Sheet>

      {/* Desktop sidebar */}
      <div
        className={`hidden lg:flex lg:flex-shrink-0 transition-all duration-200 ${
          collapsed ? "w-14" : "w-60"
        }`}
      >
        <div className="flex flex-col w-full">
          <SidebarContent isCollapsed={collapsed} />
        </div>
      </div>

      {/* Main content */}
      <div className="flex flex-1 flex-col overflow-hidden">
        {/* Mobile header */}
        <div className="lg:hidden flex items-center justify-between bg-sidebar border-b border-sidebar-border px-4 py-2">
          <div className="flex items-center">
            <PenLine className="h-6 w-6 text-sidebar-primary mr-2" />
            <span className="text-xl font-bold tracking-tight text-sidebar-foreground">ClassTrack</span>
          </div>
          <Button variant="ghost" size="icon" onClick={() => setIsMobileOpen(true)} className="text-sidebar-foreground hover:bg-sidebar-accent">
            <Menu className="h-6 w-6" />
          </Button>
        </div>

        {/* School merge banner */}
        {showMergeBanner && (
          <div
            className="bg-indigo-50 border-b border-indigo-200 px-4 py-2.5 flex items-center justify-between gap-3 flex-shrink-0"
            data-testid="banner-merge-wizard"
          >
            <div className="flex items-center gap-2.5 min-w-0">
              <Building2 className="h-4 w-4 text-indigo-600 flex-shrink-0" />
              <p className="text-xs sm:text-sm text-indigo-900 min-w-0">
                <span className="font-semibold">Your school is now connected.</span>{" "}
                We found <span className="font-semibold">{suggestionCount}</span> of your
                students that may match the school roster. Link them to keep your records
                together.
              </p>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <Button
                size="sm"
                className="h-7 bg-indigo-600 hover:bg-indigo-700 text-white"
                onClick={() => setMergeWizardOpen(true)}
                data-testid="button-merge-banner-review"
              >
                Review matches
                <ArrowRight className="h-3.5 w-3.5 ml-1" />
              </Button>
              <button
                type="button"
                title="Dismiss for now"
                className="text-indigo-400 hover:text-indigo-700 p-1"
                onClick={async () => {
                  await updateProfile.mutateAsync({
                    data: {
                      preferences: {
                        ...mergeWizardPrefs,
                        mergeWizardStatus: "dismissed",
                      },
                    },
                  });
                  queryClient.invalidateQueries({ queryKey: getGetProfileQueryKey() });
                }}
                data-testid="button-merge-banner-dismiss"
              >
                <XIcon className="h-3.5 w-3.5" />
              </button>
            </div>
          </div>
        )}


        {/* Trial banner — hidden when beta access is pending (user can't act on it) */}
        {isTrial && !isBetaPending && (
          <div
            className="bg-amber-50 border-b border-amber-200 px-4 py-2 flex items-center justify-between gap-3 flex-shrink-0"
            data-testid="banner-trial"
          >
            <p className="text-xs text-amber-800 font-medium">
              {trialDaysLeft <= 1
                ? "Your free trial ends today — subscribe to keep making changes after that."
                : `Free trial — ${trialDaysLeft} day${trialDaysLeft === 1 ? "" : "s"} remaining. Everything is included.`}
            </p>
            <Link href="/pricing" className="text-xs font-semibold text-amber-900 underline underline-offset-2 whitespace-nowrap hover:text-amber-700">
              Subscribe
            </Link>
          </div>
        )}

        {/* Payment-failed banner — hidden when beta access is pending */}
        {paymentFailed && !isBetaPending && (
          <div
            className="bg-orange-50 border-b border-orange-200 px-4 py-2 flex items-center justify-between gap-3 flex-shrink-0"
            data-testid="banner-payment-failed"
          >
            <p className="text-xs text-orange-900 font-medium">
              Your last payment was declined. Update your card to keep your subscription active —
              we'll keep trying in the meantime.
            </p>
            <button
              type="button"
              disabled={openPortalForBanner.isPending}
              onClick={async () => {
                try {
                  const { url } = await openPortalForBanner.mutateAsync();
                  window.location.href = url;
                } catch (err) {
                  appLayoutToast({
                    title: "Could not open billing portal",
                    description: getApiErrorMessage(err, "Please try again in a moment."),
                    variant: "destructive",
                  });
                }
              }}
              className="text-xs font-semibold text-orange-900 underline underline-offset-2 whitespace-nowrap hover:text-orange-700 disabled:opacity-60"
              data-testid="button-payment-failed-update-card"
            >
              {openPortalForBanner.isPending ? "Opening…" : "Update card"}
            </button>
          </div>
        )}

        {/* Read-only banner — shown after trial ends without an active subscription. Hidden when beta pending. Dismissible per session. */}
        {showReadOnlyBanner && !isBetaPending && (
          <div
            className="bg-rose-50 border-b border-rose-200 px-4 py-2 flex items-center justify-between gap-3 flex-shrink-0"
            data-testid="banner-read-only"
          >
            <p className="text-xs text-rose-900 font-medium">
              Read-only mode — your trial has ended. Your data is safe and you can still view and export everything. Subscribe to start making changes again.
            </p>
            <div className="flex items-center gap-2 flex-shrink-0">
              <Link href="/pricing" className="text-xs font-semibold text-rose-900 underline underline-offset-2 whitespace-nowrap hover:text-rose-700">
                Subscribe
              </Link>
              <button
                type="button"
                onClick={dismissReadOnlyBanner}
                aria-label="Dismiss read-only banner"
                data-testid="dismiss-banner-read-only"
                className="text-rose-900/70 hover:text-rose-900 rounded p-0.5"
              >
                <XIcon className="h-3.5 w-3.5" />
              </button>
            </div>
          </div>
        )}

        <main className="flex-1 overflow-y-auto focus:outline-none relative">
          {/* Appearance pattern layer (Task #164) — scoped strictly to the
              main scroll area so it can never bleed onto the sidebar,
              top bar, or modals/portals. */}
          <div className="app-pattern-layer" aria-hidden />
          {isBetaPending ? (
            <div className="flex items-center justify-center min-h-full p-8">
              <div className="text-center max-w-md">
                <div className="w-16 h-16 bg-indigo-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <ShieldCheck className="h-8 w-8 text-indigo-600" />
                </div>
                <h1 className="text-2xl font-bold text-gray-900 mb-3">Beta access pending</h1>
                <p className="text-gray-600 mb-4">
                  ClassTrack is currently in closed beta. We've received your registration
                  and will notify you when your account is approved.
                </p>
                <p className="text-sm text-gray-400">
                  Questions? Email us at{" "}
                  <a href="mailto:hello@classtrack.com.au" className="underline hover:text-gray-600">
                    hello@classtrack.com.au
                  </a>
                </p>
              </div>
            </div>
          ) : children}
        </main>
      </div>
    </div>
  );
}
